<!--
  How the score was reached (RF-36, RF-41, AC-20).
  MatDialog shell + Hyper markup: .modal-*, .table .table-centered, .progress.
-->
<div class="modal-header">
  <h4 class="modal-title">
    <i class="ri-focus-2-line me-1"></i>
    {{ 'kanban.scoreExplanation.title' | translate }}
  </h4>
  <button type="button" class="btn-close" (click)="close()"></button>
</div>

<div class="modal-body">
  @if (loading) {
    <div class="text-center py-4">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ 'kanban.scoreExplanation.loading' | translate }}</span>
      </div>
    </div>
  } @else if (explanation?.status !== 'OK') {
    <!-- Not computed yet, or computation failed: say which, never show a zero. -->
    <div class="alert alert-secondary d-flex align-items-center" role="alert">
      <i class="ri-question-line me-2 font-18"></i>
      <span>{{ 'kanban.scoreExplanation.unavailable' | translate }}</span>
    </div>
  } @else {
    <div class="d-flex align-items-center gap-3 mb-3">
      <div class="avatar-sm rounded-circle bg-soft-primary d-flex align-items-center justify-content-center">
        <span class="fw-bold text-primary font-18">{{ explanation.score }}</span>
      </div>
      <div>
        <h5 class="mt-0 mb-1">{{ 'kanban.scoreExplanation.scoreOf100' | translate }}</h5>
        <p class="text-muted mb-0 font-13">
          <!--
            The denominator is the APPLICABLE weight, not a fixed 100 (RF-41): a deal
            with no linked conversation is scored over the blocks that could apply.
          -->
          {{
            'kanban.scoreExplanation.availableWeight'
              | translate: { weight: explanation.availableWeight }
          }}
          @if (explanation.computedAt) {
            · {{ 'kanban.scoreExplanation.computedAt' | translate }}
            {{ explanation.computedAt | date: 'short' }}
          }
        </p>
        @if (explanation.penaltyTotal) {
          <p class="text-muted font-12 mb-0">
            {{
              'kanban.scoreExplanation.penaltyApplied'
                | translate
                  : {
                      subtotal: explanation.subtotal,
                      penalty: explanation.penaltyTotal,
                    }
            }}
          </p>
        }
      </div>
    </div>

    <h4 class="header-title mt-3">{{ 'kanban.scoreExplanation.contributed' | translate }}</h4>
    <div class="table-responsive">
      <table class="table table-centered table-hover mb-0">
        <thead class="table-light">
          <tr>
            <th>{{ 'kanban.scoreExplanation.criterion' | translate }}</th>
            <th class="text-center">{{ 'kanban.scoreExplanation.weight' | translate }}</th>
            <th class="text-center">{{ 'kanban.scoreExplanation.earned' | translate }}</th>
            <th class="score-share-col">{{ 'kanban.scoreExplanation.share' | translate }}</th>
          </tr>
        </thead>
        <tbody>
          @for (criterion of applicableCriteria; track criterion.criterion) {
            <tr>
              <td>
                <span class="fw-semibold">{{ criterionLabel(criterion.criterion) | translate }}</span>
                <br />
                <small class="text-muted">{{ blockLabel(criterion.block) | translate }}</small>
              </td>
              <td class="text-center">{{ criterion.weight }}</td>
              <td class="text-center fw-bold">{{ criterion.earned }}</td>
              <td>
                <div class="progress score-progress">
                  <div
                    class="progress-bar bg-primary"
                    role="progressbar"
                    [style.width.%]="earnedPercent(criterion)"
                    [attr.aria-valuenow]="earnedPercent(criterion)"
                    aria-valuemin="0"
                    aria-valuemax="100"
                  ></div>
                </div>
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>

    @if (notApplicableCriteria.length > 0) {
      <h4 class="header-title mt-4">
        {{ 'kanban.scoreExplanation.notApplicable' | translate }}
      </h4>
      <p class="text-muted font-13">
        {{ 'kanban.scoreExplanation.notApplicableHint' | translate }}
      </p>
      <div class="d-flex flex-wrap gap-1">
        @for (criterion of notApplicableCriteria; track criterion.criterion) {
          <span class="badge bg-soft-secondary text-muted">
            {{ criterionLabel(criterion.criterion) | translate }}
          </span>
        }
      </div>
    }
  }
</div>

<div class="modal-footer">
  <button type="button" class="btn btn-light" (click)="close()">
    {{ 'kanban.scoreExplanation.close' | translate }}
  </button>
</div>
